import * as React from "react";
import { useCallback, useContext, useMemo } from "react";
import * as Immutable from "immutable";
import styled from "styled-components";
import { AdditionalContext } from "views/logic/ActionContext";
import { useStore } from "stores/connect";
import { StreamsStore } from "views/stores/StreamsStore";
import FieldType from "views/logic/fieldtypes/FieldType";
import { MESSAGE_FIELD } from "views/Constants";
import { InputsStore } from "stores/inputs/InputsStore";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import { TableDataCell } from "views/components/datatable";
import MessageDetail from "components/common/message/details/MessageDetail";
import DecoratedValue from "views/components/messagelist/decoration/DecoratedValue";
import CustomHighlighting from "views/components/highlighting/CustomHighlighting";
import TypeSpecificValue from "views/components/TypeSpecificValue";
import HighlightMessageContext from "views/components/contexts/HighlightMessageContext";
import { toSelectableMessageTableEntry } from "views/components/widgets/MessageTableSelectedEntitiesProvider";
import RowCheckbox from "components/common/EntityDataTable/RowCheckbox";
import BulkSelectCell from "components/common/message/messagetable/BulkSelectCell";
import useSelectedMessageEntities from "views/hooks/useSelectedMessageEntities";
import MessagePreview from "./MessagePreview";
export const TableBody = styled.tbody(
  ({ $expanded, $highlighted, theme }) => `
  && {
    border-top: 0;

    ${$expanded ? `
  border-left: 7px solid ${theme.colors.variant.light.info};
` : ""}

    ${$highlighted ? `
  border-left: 7px solid ${theme.colors.variant.light.success};
` : ""}
  }
`
);
const FieldsRow = styled.tr`
  cursor: pointer;

  && td {
    min-width: 50px;
    word-break: break-word;
    padding: 4px 5px 2px;
  }

  time {
    line-height: 1;
  }
`;
const ActionsCell = styled(TableDataCell)`
  width: 1%;
  white-space: nowrap;
  text-align: right;
`;
const MessageDetailRow = styled.tr`
  td {
    padding-top: 5px;
    border-top: 0;
  }

  .row {
    margin-right: 0;
  }

  div[class*='col-'] {
    padding-right: 0;
  }
`;
const isDecoratedField = (field, decorationStats) => decorationStats && (decorationStats.added_fields[field] !== void 0 || decorationStats.changed_fields[field] !== void 0);
const fieldType = (fieldName, { decoration_stats: decorationStats }, fields) => {
  var _a;
  return isDecoratedField(fieldName, decorationStats) ? FieldType.Decorated : ((_a = fields == null ? void 0 : fields.find((f) => f.name === fieldName)) != null ? _a : { type: FieldType.Unknown }).type;
};
const Strong = ({ children = void 0, strong }) => strong ? /* @__PURE__ */ React.createElement("strong", null, children) : /* @__PURE__ */ React.createElement(React.Fragment, null, children);
const MessageTableEntry = ({
  config,
  disableSurroundingSearch = false,
  expandAllRenderAsync,
  expanded,
  fields,
  message,
  rowActions = void 0,
  showMessageRow = false,
  selectedFields = Immutable.OrderedSet(),
  toggleDetail,
  displayBulkSelectCol = false,
  isEntitySelectable = () => false
}) => {
  const { inputs: inputsList = [] } = useStore(InputsStore);
  const { streams: streamsList = [] } = useStore(StreamsStore);
  const highlightMessageId = useContext(HighlightMessageContext);
  const { toggleEntitySelect, isEntitySelected } = useSelectedMessageEntities();
  const sendTelemetry = useSendTelemetry();
  const additionalContextValue = useMemo(() => ({ message }), [message]);
  const allStreams = useMemo(() => Immutable.List(streamsList), [streamsList]);
  const streams = useMemo(
    () => Immutable.Map(streamsList.map((stream) => [stream.id, stream])),
    [streamsList]
  );
  const inputs = useMemo(
    () => Immutable.Map(inputsList.map((input) => [input.id, input])),
    [inputsList]
  );
  const _toggleDetail = useCallback(() => {
    var _a;
    const isSelectingText = !!((_a = window.getSelection()) == null ? void 0 : _a.toString());
    if (!isSelectingText) {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_MESSAGE_TABLE_DETAILS_TOGGLED, {
        app_section: "widget",
        app_action_value: "widget-message-table-toggle-details"
      });
      toggleDetail(`${message.index}-${message.id}`);
    }
  }, [message.id, message.index, sendTelemetry, toggleDetail]);
  const isSelected = isEntitySelected(message.index, message.id);
  const checkboxTitle = `${isSelected ? "Deselect" : "Select"} message`;
  const isSelectDisabled = !displayBulkSelectCol || !isEntitySelectable(toSelectableMessageTableEntry(message));
  const colSpanFixup = selectedFields.size + 1 + (rowActions ? 1 : 0) + (displayBulkSelectCol ? 1 : 0);
  const selectedFieldsList = useMemo(
    () => selectedFields.toArray().map((selectedFieldName, idx) => {
      const type = fieldType(selectedFieldName, message, fields);
      return /* @__PURE__ */ React.createElement(
        TableDataCell,
        {
          className: "table-data-cell",
          $isNumeric: type.isNumeric(),
          key: selectedFieldName,
          "data-testid": `message-summary-field-${selectedFieldName}`
        },
        /* @__PURE__ */ React.createElement(Strong, { strong: idx === 0 }, /* @__PURE__ */ React.createElement(CustomHighlighting, { field: selectedFieldName, value: message.fields[selectedFieldName] }, /* @__PURE__ */ React.createElement(
          TypeSpecificValue,
          {
            value: message.fields[selectedFieldName],
            field: selectedFieldName,
            type,
            render: DecoratedValue
          }
        )))
      );
    }),
    [fields, message, selectedFields]
  );
  const messageFieldType = useMemo(() => fieldType(MESSAGE_FIELD, message, fields), [fields, message]);
  return /* @__PURE__ */ React.createElement(AdditionalContext.Provider, { value: additionalContextValue }, /* @__PURE__ */ React.createElement(TableBody, { $expanded: expanded, $highlighted: message.id === highlightMessageId }, /* @__PURE__ */ React.createElement(FieldsRow, { onClick: _toggleDetail, className: "table-data-row" }, displayBulkSelectCol && /* @__PURE__ */ React.createElement(BulkSelectCell, null, /* @__PURE__ */ React.createElement(
    RowCheckbox,
    {
      onChange: () => toggleEntitySelect(message.index, message.id),
      title: !isSelectDisabled ? checkboxTitle : void 0,
      checked: isSelected,
      disabled: isSelectDisabled,
      "aria-label": checkboxTitle
    }
  )), selectedFieldsList, rowActions && /* @__PURE__ */ React.createElement(ActionsCell, { $isNumeric: false }, rowActions)), /* @__PURE__ */ React.createElement(
    MessagePreview,
    {
      showMessageRow,
      config,
      colSpanFixup,
      messageFieldType,
      onRowClick: _toggleDetail,
      message,
      displayBulkSelectCol
    }
  ), expanded && /* @__PURE__ */ React.createElement(MessageDetailRow, null, /* @__PURE__ */ React.createElement("td", { colSpan: colSpanFixup }, /* @__PURE__ */ React.createElement(
    MessageDetail,
    {
      message,
      fields,
      streams,
      allStreams,
      inputs,
      disableSurroundingSearch,
      expandAllRenderAsync
    }
  )))));
};
export default MessageTableEntry;
